import React, { useEffect, useRef, useState } from 'react';
import { Icon, Spin } from '../components/ui.jsx';
import { api, fmt, fmtD } from '../api.js';
import { SL, RL } from '../constants.js';

const COLORS = {
  project: '#31367A', client: '#E6960A', technician: '#0E9F6E',
};
const TYPE_LABELS = {
  project: 'Cantiere', client: 'Cliente', technician: 'Tecnico',
};
const EDGE_LABELS = {
  commissionato_da: 'Committente',
  ha_lavorato_a: 'Ha lavorato',
};
const ICONS = {
  project: 'M4 20h16a2 2 0 002-2V8l-4-4H6a2 2 0 00-2 2v12a2 2 0 002 2z M2 10h20',
  client: 'M6 22V4a2 2 0 012-2h8a2 2 0 012 2v18Z M6 12H4a2 2 0 00-2 2v6a2 2 0 002 2h2 M18 12h2a2 2 0 012 2v6a2 2 0 01-2 2h-2',
  technician: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 3a4 4 0 100 8 4 4 0 000-8z',
};

function whiteIconUri(type) {
  const path = ICONS[type] || ICONS.project;
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`
  );
}
function coloredIconUri(type) {
  const path = ICONS[type] || ICONS.project;
  const c = COLORS[type] || '#999';
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`
  );
}

function loadD3() {
  return new Promise((resolve) => {
    if (window.d3) return resolve(window.d3);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js';
    s.onload = () => resolve(window.d3);
    document.head.appendChild(s);
  });
}

export const GraphPage = ({onNav}) => {
  const svgRef = useRef(null);
  const simRef = useRef(null);
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDD, setShowDD] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailNeighbors, setDetailNeighbors] = useState([]);
  const [chips, setChips] = useState({});
  const [loading, setLoading] = useState(true);
  const [graphStats, setGraphStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      const d3 = await loadD3();
      if (cancelled || !svgRef.current) return;

      try {
        const data = await fetch('/api/graph').then(r => r.json());
        const nodes = (data.nodes || []).map(n => ({...n, name: n.label}));
        const edges = (data.edges || []).map(e => ({
          source: e.source, target: e.target,
          edge_type: e.type, role: e.role,
        }));
        setAllNodes(nodes);
        setAllEdges(edges);
        setGraphStats(data.stats);

        // Chips
        const m = {};
        nodes.forEach(n => { m[n.type] = (m[n.type]||0)+1; });
        setChips(m);

        // SVG setup
        const container = svgRef.current.parentElement;
        const W = container.clientWidth;
        const H = container.clientHeight;
        const svg = d3.select(svgRef.current).attr('width', W).attr('height', H);
        svg.selectAll('*').remove();

        // Zoom
        const g = svg.append('g');
        svg.call(d3.zoom().scaleExtent([0.1, 6]).on('zoom', (ev) => {
          g.attr('transform', ev.transform);
        }));

        // Edges
        const link = g.append('g').selectAll('line').data(edges).join('line')
          .attr('stroke', 'rgba(148,163,184,0.15)')
          .attr('stroke-width', 0.8);

        // Node groups
        const node = g.append('g').selectAll('g').data(nodes).join('g')
          .style('cursor', 'pointer')
          .call(d3.drag()
            .on('start', (ev, d) => {
              if (!ev.active) simRef.current.alphaTarget(0.3).restart();
              d.fx = d.x; d.fy = d.y;
            })
            .on('drag', (ev, d) => { d.fx = ev.x; d.fy = ev.y; })
            .on('end', (ev, d) => {
              if (!ev.active) simRef.current.alphaTarget(0);
              d.fx = null; d.fy = null;
            })
          )
          .on('click', (ev, d) => {
            ev.stopPropagation();
            selectNode(d, link, node);
          });

        // Circle
        node.append('circle')
          .attr('r', d => d.type === 'client' ? 18 : d.type === 'project' ? 14 : 11)
          .attr('fill', d => COLORS[d.type] || '#94a3b8')
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.7);

        // Icon
        node.append('image')
          .attr('x', -9).attr('y', -9).attr('width', 18).attr('height', 18)
          .attr('href', d => whiteIconUri(d.type));

        // Label
        node.append('text')
          .attr('dy', d => (d.type === 'client' ? 32 : d.type === 'project' ? 28 : 24))
          .attr('text-anchor', 'middle')
          .attr('fill', '#94a3b8').attr('font-size', '10px')
          .attr('font-family', 'var(--font), Inter, system-ui, sans-serif')
          .attr('font-weight', 400)
          .text(d => (d.label || '').substring(0, 22));

        // Force simulation
        const sim = d3.forceSimulation(nodes)
          .force('link', d3.forceLink(edges).id(d => d.id).distance(120))
          .force('charge', d3.forceManyBody().strength(-300))
          .force('center', d3.forceCenter(W / 2, H / 2))
          .force('collision', d3.forceCollide().radius(30));

        simRef.current = sim;

        sim.on('tick', () => {
          link.attr('x1', d => d.source.x).attr('y1', d => d.source.y)
              .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
          node.attr('transform', d => `translate(${d.x},${d.y})`);
        });

        // Background click → deselect
        svg.on('click', () => {
          node.select('circle').attr('stroke', '#fff').attr('stroke-width', 1.5).attr('stroke-opacity', 0.7);
          link.attr('stroke', 'rgba(148,163,184,0.15)').attr('stroke-width', 0.8);
          node.style('opacity', 1);
          setDetail(null);
        });

        setLoading(false);
      } catch(err) {
        console.error('Graph load error:', err);
        setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; if (simRef.current) simRef.current.stop(); };
  }, []);

  // Search
  useEffect(() => {
    if (searchQuery.length < 2) { setResults([]); return; }
    const q = searchQuery.toLowerCase();
    setResults(allNodes.filter(n =>
      (n.label||'').toLowerCase().includes(q) || (n.code||'').toLowerCase().includes(q)
    ).slice(0, 15));
  }, [searchQuery, allNodes]);

  function selectNode(d, linkSel, nodeSel) {
    const connected = new Set();
    connected.add(d.id);
    linkSel.each(function(e) {
      const sid = typeof e.source === 'object' ? e.source.id : e.source;
      const tid = typeof e.target === 'object' ? e.target.id : e.target;
      if (sid === d.id) connected.add(tid);
      if (tid === d.id) connected.add(sid);
    });
    nodeSel.style('opacity', n => connected.has(n.id) ? 1 : 0.08);
    nodeSel.select('circle')
      .attr('stroke', n => n.id === d.id ? '#facc15' : '#fff')
      .attr('stroke-width', n => n.id === d.id ? 3 : 1.5)
      .attr('stroke-opacity', 1);
    linkSel
      .attr('stroke', e => {
        const sid = typeof e.source === 'object' ? e.source.id : e.source;
        const tid = typeof e.target === 'object' ? e.target.id : e.target;
        return (sid === d.id || tid === d.id) ? 'rgba(49,54,122,0.5)' : 'rgba(148,163,184,0.04)';
      })
      .attr('stroke-width', e => {
        const sid = typeof e.source === 'object' ? e.source.id : e.source;
        const tid = typeof e.target === 'object' ? e.target.id : e.target;
        return (sid === d.id || tid === d.id) ? 2 : 0.5;
      });

    // Build neighbors directly from D3 link data (source/target are live objects after forceLink)
    const neighbors = [];
    linkSel.each(function(e) {
      const src = typeof e.source === 'object' ? e.source : null;
      const tgt = typeof e.target === 'object' ? e.target : null;
      if (!src || !tgt) return;
      if (src.id === d.id) {
        neighbors.push({...tgt, edge_type: e.edge_type, role: e.role, direction: 'outgoing'});
      }
      if (tgt.id === d.id) {
        neighbors.push({...src, edge_type: e.edge_type, role: e.role, direction: 'incoming'});
      }
    });
    setDetail(d);
    setDetailNeighbors(neighbors);
  }

  function focusNode(id) {
    setShowDD(false); setSearchQuery('');
    const d3 = window.d3;
    if (!d3 || !svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const g = svg.select('g');
    const nodeSel = g.selectAll('g g');
    const linkSel = g.selectAll('g line');
    const target = allNodes.find(n => n.id === id);
    if (target) {
      const container = svgRef.current.parentElement;
      const W = container.clientWidth, H = container.clientHeight;
      svg.transition().duration(500).call(
        d3.zoom().scaleExtent([0.1, 6]).on('zoom', (ev) => g.attr('transform', ev.transform)).transform,
        d3.zoomIdentity.translate(W/2, H/2).scale(1.5).translate(-target.x, -target.y)
      );
      setTimeout(() => selectNode(target, linkSel, nodeSel), 550);
    }
  }

  return (
    <div style={{height:'calc(100vh - 70px)',display:'flex',overflow:'hidden',background:'#f8fafc',
      borderRadius:12,border:'1px solid var(--border)'}}>
      <div style={{flex:1,position:'relative',minWidth:0}}>
        {/* Search */}
        <div style={{position:'absolute',top:12,left:12,right:detail?372:12,zIndex:10,display:'flex',gap:8}}>
          <div style={{flex:1,position:'relative'}}>
            <Icon name="search" size={15} style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'#94a3b8'}}/>
            <input placeholder="Cerca cantieri, tecnici, clienti..." value={searchQuery}
              onChange={e=>{setSearchQuery(e.target.value);setShowDD(true);}}
              onFocus={()=>searchQuery.length>=2&&setShowDD(true)}
              style={{width:'100%',padding:'9px 14px 9px 34px',fontSize:13,background:'white',
                border:'1px solid #e2e8f0',borderRadius:10,boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
                outline:'none',fontFamily:'var(--font)'}}/>
            {showDD && results.length > 0 && (
              <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:4,
                background:'white',border:'1px solid #e2e8f0',borderRadius:10,
                maxHeight:340,overflowY:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.12)',zIndex:20}}>
                {results.map(n=>(
                  <div key={n.id} onClick={()=>focusNode(n.id)}
                    style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid #f1f5f9'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <span style={{width:10,height:10,borderRadius:'50%',background:COLORS[n.type]||'#999',flexShrink:0}}/>
                    <span style={{fontSize:13,fontWeight:500,flex:1}}>{n.label}</span>
                    <span style={{fontSize:10,color:'#94a3b8',textTransform:'uppercase'}}>{TYPE_LABELS[n.type]||n.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* D3 SVG */}
        <svg ref={svgRef} style={{width:'100%',height:'100%',background:'#f8fafc'}}/>

        {loading && (
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(248,250,252,0.9)',zIndex:15}}>
            <div style={{textAlign:'center'}}>
              <Spin/><p style={{marginTop:12,fontSize:14,color:'#64748b'}}>Caricamento grafo...</p>
            </div>
          </div>
        )}

        {/* Chips */}
        {Object.keys(chips).length > 0 && (
          <div style={{position:'absolute',bottom:12,left:12,display:'flex',gap:5,zIndex:10,flexWrap:'wrap'}}>
            {Object.entries(chips).sort((a,b)=>b[1]-a[1]).map(([type,count])=>(
              <div key={type} style={{background:'white',border:'1px solid #e2e8f0',borderRadius:16,padding:'3px 10px',fontSize:10,color:'#64748b',display:'flex',alignItems:'center',gap:5,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:COLORS[type]||'#999'}}/>
                {TYPE_LABELS[type]||type} {count}
              </div>
            ))}
            {graphStats && (
              <div style={{background:'white',border:'1px solid #e2e8f0',borderRadius:16,padding:'3px 10px',fontSize:10,color:'#94a3b8',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                {graphStats.projects+graphStats.clients+graphStats.technicians} nodi · {graphStats.connections} archi
              </div>
            )}
          </div>
        )}
      </div>

      {/* Side panel */}
      {detail && (
        <div style={{width:360,background:'white',borderLeft:'1px solid #e2e8f0',display:'flex',flexDirection:'column',overflow:'hidden',flexShrink:0}}>
          <div style={{padding:20,display:'flex',alignItems:'center',gap:14,borderBottom:'1px solid #f1f5f9'}}>
            <div style={{width:44,height:44,borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',background:(COLORS[detail.type]||'#999')+'18',flexShrink:0}}>
              <img src={coloredIconUri(detail.type)} width={22} height={22} alt=""/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:16,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{detail.label}</div>
              <div style={{fontSize:11,textTransform:'uppercase',letterSpacing:'.05em',color:COLORS[detail.type]||'#999',fontWeight:600}}>
                {TYPE_LABELS[detail.type]||detail.type}
              </div>
            </div>
            <button onClick={()=>{setDetail(null);/* reset via svg click */}}
              style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:4}}>
              <Icon name="x" size={16}/>
            </button>
          </div>

          {/* Detail info */}
          {detail.type==='project' && (
            <div style={{padding:'12px 20px',fontSize:12,color:'#64748b',lineHeight:1.6,borderBottom:'1px solid #f1f5f9'}}>
              {detail.code && <div><span style={{fontWeight:600,color:'var(--blue)'}}>{detail.code}</span></div>}
              {detail.date_start && <div>{fmtD(detail.date_start)}</div>}
              {detail.status && <div>{SL[detail.status]||detail.status}</div>}
              {detail.tipologia && <div>{detail.tipologia}</div>}
              {onNav && (
                <button className="btn btn-primary btn-sm" style={{marginTop:8,width:'100%'}}
                  onClick={()=>onNav('projects', +detail.id.split('-')[1])}>
                  <Icon name="external-link" size={12} color="#fff"/> Apri cantiere
                </button>
              )}
            </div>
          )}
          {detail.type==='technician' && (
            <div style={{padding:'12px 20px',borderBottom:'1px solid #f1f5f9'}}>
              <span className={`emp-badge emp-${detail.employment_type||'freelance'}`}>{detail.employment_type}</span>
            </div>
          )}

          {/* Connections */}
          <div style={{flex:1,overflowY:'auto',padding:'14px 20px'}}>
            <h4 style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.1em',color:'#94a3b8',margin:'0 0 10px'}}>
              Connessioni ({detailNeighbors.length})
            </h4>
            {detailNeighbors.map((n,i)=>(
              <div key={i} onClick={()=>focusNode(n.id)}
                style={{display:'flex',alignItems:'center',gap:10,padding:'7px 8px',borderRadius:7,cursor:'pointer',marginBottom:2}}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                <div style={{width:28,height:28,borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center',background:(COLORS[n.type]||'#999')+'15',flexShrink:0}}>
                  <img src={coloredIconUri(n.type)} width={14} height={14} alt=""/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{n.label}</div>
                  <div style={{fontSize:10,color:'#94a3b8',fontFamily:'monospace',display:'flex',alignItems:'center',gap:4}}>
                    <Icon name={n.direction==='outgoing'?'arrow-right':'arrow-left'} size={9}
                      color={n.direction==='outgoing'?'var(--blue)':'#0E9F6E'}/>
                    {EDGE_LABELS[n.edge_type]||n.edge_type}
                    {n.role && <span style={{marginLeft:4,color:'#94a3b8'}}>({RL[n.role]||n.role})</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
